import axios from 'axios';
import { getMockData } from './mockData';
import { scrapeAmazonCategories, getMarketplaceDomain } from './scraperApi';

const API_SECRET_KEY = process.env.REACT_APP_API_SECRET_KEY;

// Mock data mode switch - automatically switches to mock data when API key is invalid
let useMockData = false;

// Scraper mode switch
let useScraperMode = true; // Default to scraper mode

// Use local proxy to avoid CORS issues
const apiClient = axios.create({
  baseURL: '/api',
  headers: {
    'secret-key': API_SECRET_KEY,
  },
});

/**
 * Set whether to use mock data
 * @param {boolean} useMock - Whether to use mock data
 */
export const setUseMockData = (useMock) => {
  useMockData = useMock;
  localStorage.setItem('useMockData', useMock ? 'true' : 'false');
};

/**
 * Get whether currently using mock data
 * @returns {boolean}
 */
export const getUseMockData = () => {
  const stored = localStorage.getItem('useMockData');
  if (stored !== null) {
    useMockData = stored === 'true';
  }
  return useMockData;
};

/**
 * Set whether to use scraper mode
 * @param {boolean} useScraper - Whether to use scraper mode
 */
export const setUseScraperMode = (useScraper) => {
  useScraperMode = useScraper;
  localStorage.setItem('useScraperMode', useScraper ? 'true' : 'false');
};

/**
 * Get whether currently using scraper mode
 * @returns {boolean}
 */
export const getUseScraperMode = () => {
  const stored = localStorage.getItem('useScraperMode');
  if (stored !== null) {
    useScraperMode = stored === 'true';
  }
  return useScraperMode;
};

/**
 * Query product categories
 * @param {Object} params - Query parameters
 * @param {string} params.marketplace - Marketplace (e.g., US, UK, DE, FR, IT, ES, CA, JP)
 * @param {string} params.month - Query history month, format yyyyMM (e.g., 202502)
 * @param {string} [params.nodeIdPath] - Category node id string (e.g., 2619525011:3741271)
 * @param {string} [params.keyword] - Search keyword, nodeId or category name (e.g., Books)
 * @returns {Promise} API response
 */
export const getProductCategories = async (params) => {
  // If using mock data mode
  if (getUseMockData()) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(getMockData(params.marketplace, params.month, params.nodeIdPath, params.keyword));
      }, 500); // Simulate network delay
    });
  }

  // If using scraper mode
  if (getUseScraperMode()) {
    try {
      console.log('Using scraper mode to fetch Amazon data...');
      const marketplaceDomain = getMarketplaceDomain(params.marketplace);
      const response = await scrapeAmazonCategories({
        marketplace: marketplaceDomain,
        refresh: false,
      });
      return response;
    } catch (error) {
      console.error('Scraper Error:', error);
      // If scraper fails, fall back to mock data
      console.warn('Scraper failed, switching to mock data mode');
      setUseMockData(true);
      return getMockData(params.marketplace, params.month, params.nodeIdPath, params.keyword);
    }
  }

  // Try using real API (Sellersprite)
  try {
    const response = await apiClient.get('/product/node', { params });
    return response.data;
  } catch (error) {
    console.error('API Error:', error);

    // If authentication error, automatically switch to mock data mode
    if (error.response?.data?.code === 'ERROR_UNAUTHORIZED' ||
        error.response?.status === 401 ||
        error.message.includes('Network Error')) {
      console.warn('API call failed, automatically switching to mock data mode');
      setUseMockData(true);
      return getMockData(params.marketplace, params.month, params.nodeIdPath, params.keyword);
    }

    throw error;
  }
};

/**
 * Save category data to local storage
 * @param {Array} categories - Category data array
 * @param {Object} queryParams - Query parameters
 */
export const saveCategoriesToLocal = (categories, queryParams) => {
  const timestamp = new Date().toISOString();
  const savedData = {
    timestamp,
    queryParams,
    categories,
    count: categories.length,
  };

  // Save to localStorage
  const key = `categories_${queryParams.marketplace}_${queryParams.month}`;
  localStorage.setItem(key, JSON.stringify(savedData));

  // Save to history
  const historyKey = 'search_history';
  const history = JSON.parse(localStorage.getItem(historyKey) || '[]');
  history.unshift({
    timestamp,
    queryParams,
    count: categories.length,
  });
  // Only keep the most recent 20 history items
  localStorage.setItem(historyKey, JSON.stringify(history.slice(0, 20)));

  return savedData;
};

/**
 * Load category data from local storage
 * @param {string} marketplace - Marketplace
 * @param {string} month - Month
 * @returns {Object|null} Saved data or null
 */
export const loadCategoriesFromLocal = (marketplace, month) => {
  const key = `categories_${marketplace}_${month}`;
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : null;
};

/**
 * Get search history
 * @returns {Array} Search history array
 */
export const getSearchHistory = () => {
  const historyKey = 'search_history';
  return JSON.parse(localStorage.getItem(historyKey) || '[]');
};

/**
 * Export data as JSON file
 * @param {Object} data - Data to export
 * @param {string} filename - Filename
 */
export const exportToJSON = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
